// WebAudio synthesizer for FRAG OUT. No assets, no dependencies.
//
// Nothing is created until unlock() runs from a user gesture (the play button).
// Every public method is a no op before that, or if the AudioContext could not
// be made. All sounds come from oscillators, a noise buffer, gain envelopes and
// biquad filters.
//
// Listener: setListener(x, y, z, yaw). Yaw 0 faces +z and right is +x, the same
// convention as the sim (forward = sin yaw, cos yaw; right = cos yaw, -sin yaw).
// Positional sounds attenuate by distance, full volume inside 3 m and silent
// past 40 m, roughly inverse in between, and pan from the listener's right
// vector through a StereoPannerNode. A sound at the listener is centred.
//
// Voices are capped at 24. When a new sound would go over, the quietest voice
// is dropped, which may be the new one.

const MAX_VOICES = 24;
const NEAR_M = 3;
const FAR_M = 40;
const MASTER_GAIN = 0.8;
const KIND_IMPACT = 2;   // Kind.Impact in packages/shared/src/tuning.ts
const KIND_WARHEAD = 5;  // Kind.Warhead
const HISS_LOW_HZ = 300;
const HISS_HIGH_HZ = 1200;
const BOUNCE_SPEED_CAP = 20;
const SILENT = 0.0005;   // exponential ramps cannot reach zero

interface Voice {
  out: GainNode;                        // the sound's own envelope sits before this
  space: GainNode;                      // distance attenuation
  pan: StereoPannerNode | null;         // null for sounds at the listener
  level: number;                        // peak after distance, for choosing what to drop
  end: number;                          // context time when it is done, Infinity for hisses
  sources: AudioScheduledSourceNode[];
  hissId?: string;
  pos?: { x: number; y: number; z: number }; // last known world position, for hisses
}

interface Hiss {
  voice: Voice;
  osc: OscillatorNode;
  band: BiquadFilterNode;
  noiseBand: BiquadFilterNode;
  peak: number;
}

type OscType = "sine" | "square" | "sawtooth" | "triangle";

export class Sfx {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private voices: Voice[] = [];
  private hisses = new Map<string, Hiss>();
  private ear = { x: 0, y: 0, z: 0, yaw: 0 };
  private failed = false;

  constructor() {}

  /** Call from a user gesture. Creates the context and resumes it. Safe to call again. */
  unlock(): void {
    if (this.failed) return;
    if (this.ctx) {
      if (this.ctx.state === "suspended") void this.ctx.resume().catch(() => {});
      return;
    }
    try {
      const Ctor = typeof AudioContext !== "undefined"
        ? AudioContext
        : (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) { this.failed = true; return; }
      const c = new Ctor();
      const master = c.createGain();
      master.gain.value = MASTER_GAIN;
      // A limiter after the mix so 24 voices cannot clip the output.
      const limiter = c.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.knee.value = 6;
      limiter.ratio.value = 8;
      limiter.attack.value = 0.002;
      limiter.release.value = 0.12;
      master.connect(limiter);
      limiter.connect(c.destination);
      // One second of white noise, looped by every noise voice.
      const len = Math.max(1, Math.floor(c.sampleRate));
      const buf = c.createBuffer(1, len, c.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
      this.ctx = c;
      this.master = master;
      this.noiseBuffer = buf;
      if (c.state === "suspended") void c.resume().catch(() => {});
    } catch {
      this.ctx = null;
      this.master = null;
      this.noiseBuffer = null;
      this.failed = true;
    }
  }

  get unlocked(): boolean {
    return this.ctx !== null;
  }

  /** Master volume, 0 to 1. */
  setVolume(v: number): void {
    if (!this.master || !this.ctx) return;
    this.master.gain.setTargetAtTime(clamp01(v) * MASTER_GAIN, this.ctx.currentTime, 0.02);
  }

  setListener(x: number, y: number, z: number, yaw: number): void {
    this.ear.x = x;
    this.ear.y = y;
    this.ear.z = z;
    this.ear.yaw = yaw;
    if (!this.ctx) return;
    // Running hisses follow the ear.
    for (const h of this.hisses.values()) this.respace(h);
  }

  // ---- grenade sounds

  /** Short metallic ping. */
  pin(x: number, y: number, z: number): void {
    const v = this.voice(0.35, 0.25, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    // Two inharmonic partials give the metal, a tiny noise click gives the strike.
    const env = c.createGain();
    envelope(env.gain, t, 0.002, 1, 0.18);
    env.connect(v.out);
    this.osc(v, "triangle", 2400, t, t + 0.25).connect(env);
    const p2 = c.createGain();
    p2.gain.value = 0.5;
    p2.connect(env);
    this.osc(v, "sine", 3620, t, t + 0.25).connect(p2);
    const click = c.createGain();
    envelope(click.gain, t, 0.001, 0.4, 0.015);
    click.connect(v.out);
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 3000;
    hp.connect(click);
    this.noise(v, t, t + 0.03).connect(hp);
  }

  /** Fuse hiss, pitch rising with progress. Replaces any hiss with the same id. */
  hissStart(id: string, x: number, y: number, z: number): void {
    if (!this.ctx) return;
    if (this.hisses.has(id)) this.hissStop(id);
    const peak = 0.2;
    const v = this.voice(peak, Infinity, x, y, z);
    if (!v) return;
    v.hissId = id;
    const c = this.ctx;
    const t = c.currentTime;
    v.out.gain.setValueAtTime(0, t);
    v.out.gain.linearRampToValueAtTime(1, t + 0.04);
    // A sawtooth through a bandpass gives the whistle, noise through a wider band the hiss.
    const band = c.createBiquadFilter();
    band.type = "bandpass";
    band.frequency.value = HISS_LOW_HZ * 2;
    band.Q.value = 6;
    band.connect(v.out);
    const osc = this.osc(v, "sawtooth", HISS_LOW_HZ, t, Infinity);
    osc.connect(band);
    const noiseBand = c.createBiquadFilter();
    noiseBand.type = "bandpass";
    noiseBand.frequency.value = HISS_LOW_HZ * 3;
    noiseBand.Q.value = 1.5;
    const noiseGain = c.createGain();
    noiseGain.gain.value = 0.6;
    noiseBand.connect(noiseGain);
    noiseGain.connect(v.out);
    this.noise(v, t, Infinity).connect(noiseBand);
    this.hisses.set(id, { voice: v, osc, band, noiseBand, peak });
  }

  hissUpdate(id: string, progress01: number, x: number, y: number, z: number): void {
    if (!this.ctx) return;
    const h = this.hisses.get(id);
    if (!h) return;
    const t = this.ctx.currentTime;
    const p = clamp01(progress01);
    const f = HISS_LOW_HZ + (HISS_HIGH_HZ - HISS_LOW_HZ) * p * p;
    h.osc.frequency.setTargetAtTime(f, t, 0.03);
    h.band.frequency.setTargetAtTime(f * 2, t, 0.03);
    h.noiseBand.frequency.setTargetAtTime(f * 3, t, 0.03);
    h.voice.pos = { x, y, z };
    this.respace(h);
  }

  hissStop(id: string): void {
    if (!this.ctx) return;
    const h = this.hisses.get(id);
    if (!h) return;
    this.hisses.delete(id);
    const t = this.ctx.currentTime;
    const v = h.voice;
    v.hissId = undefined;
    v.out.gain.cancelScheduledValues(t);
    v.out.gain.setValueAtTime(v.out.gain.value, t);
    v.out.gain.linearRampToValueAtTime(0, t + 0.05);
    v.end = t + 0.08;
    for (const s of v.sources) {
      try { s.stop(v.end); } catch { /* already stopped */ }
    }
  }

  /** A knock whose volume follows speed. Three variants picked by position. */
  bounce(x: number, y: number, z: number, speed: number): void {
    const s = clamp01(Math.abs(speed) / BOUNCE_SPEED_CAP);
    if (s < 0.02) return;
    const loud = Math.sqrt(s);
    const variant = hash3(x, y, z) % 3;
    const v = this.voice(0.45 * loud, 0.3, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    if (variant === 0) {
      // Concrete tap: a short lowpassed noise burst with a little body.
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 1400;
      const env = c.createGain();
      envelope(env.gain, t, 0.001, 1, 0.045);
      lp.connect(env);
      env.connect(v.out);
      this.noise(v, t, t + 0.06).connect(lp);
      const body = c.createGain();
      envelope(body.gain, t, 0.001, 0.5, 0.05);
      body.connect(v.out);
      const o = this.osc(v, "sine", 240, t, t + 0.08);
      o.frequency.exponentialRampToValueAtTime(120, t + 0.06);
      o.connect(body);
    } else if (variant === 1) {
      // Tank clang: two detuned triangles ringing out.
      const env = c.createGain();
      envelope(env.gain, t, 0.002, 0.8, 0.22);
      env.connect(v.out);
      this.osc(v, "triangle", 520, t, t + 0.3).connect(env);
      const p2 = c.createGain();
      p2.gain.value = 0.6;
      p2.connect(env);
      this.osc(v, "triangle", 790, t, t + 0.3).connect(p2);
      const strike = c.createGain();
      envelope(strike.gain, t, 0.001, 0.5, 0.02);
      strike.connect(v.out);
      const bp = c.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 2500;
      bp.Q.value = 1;
      bp.connect(strike);
      this.noise(v, t, t + 0.03).connect(bp);
    } else {
      // Plank knock: a dropping sine thump with a dull click.
      const env = c.createGain();
      envelope(env.gain, t, 0.001, 1, 0.09);
      env.connect(v.out);
      const o = this.osc(v, "sine", 200, t, t + 0.12);
      o.frequency.exponentialRampToValueAtTime(90, t + 0.08);
      o.connect(env);
      const click = c.createGain();
      envelope(click.gain, t, 0.001, 0.35, 0.02);
      click.connect(v.out);
      const lp = c.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.value = 900;
      lp.connect(click);
      this.noise(v, t, t + 0.03).connect(lp);
    }
  }

  /** Low thump plus a short high crack. Bigger and lower for Warhead, small and sharp for Impact. */
  explosion(x: number, y: number, z: number, kind: number): void {
    let peak = 0.9, thumpFrom = 130, thumpTo = 40, thumpDur = 0.45, crackDur = 0.06, crackHp = 1800, crackGain = 0.6, bodyDur = 0.3;
    if (kind === KIND_WARHEAD) {
      peak = 1.0; thumpFrom = 90; thumpTo = 24; thumpDur = 1.0; crackDur = 0.12; crackHp = 900; crackGain = 0.7; bodyDur = 0.7;
    } else if (kind === KIND_IMPACT) {
      peak = 0.7; thumpFrom = 220; thumpTo = 70; thumpDur = 0.18; crackDur = 0.05; crackHp = 2600; crackGain = 0.8; bodyDur = 0.12;
    }
    const total = Math.max(thumpDur, bodyDur, crackDur) + 0.1;
    const v = this.voice(peak, total, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    // Thump: a sine falling in pitch.
    const thump = c.createGain();
    envelope(thump.gain, t, 0.004, 1, thumpDur);
    thump.connect(v.out);
    const o = this.osc(v, "sine", thumpFrom, t, t + thumpDur + 0.05);
    o.frequency.exponentialRampToValueAtTime(thumpTo, t + thumpDur * 0.7);
    o.connect(thump);
    // Body: a band of noise around 250 Hz for the rumble.
    const bodyBand = c.createBiquadFilter();
    bodyBand.type = "bandpass";
    bodyBand.frequency.value = kind === KIND_WARHEAD ? 160 : 260;
    bodyBand.Q.value = 0.8;
    const body = c.createGain();
    envelope(body.gain, t, 0.005, 0.6, bodyDur);
    bodyBand.connect(body);
    body.connect(v.out);
    this.noise(v, t, t + bodyDur + 0.05).connect(bodyBand);
    // Crack: highpassed noise, short.
    const hp = c.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = crackHp;
    const crack = c.createGain();
    envelope(crack.gain, t, 0.001, crackGain, crackDur);
    hp.connect(crack);
    crack.connect(v.out);
    this.noise(v, t, t + crackDur + 0.02).connect(hp);
  }

  // ---- knife and kick

  /** Stab whoosh. */
  stab(x: number, y: number, z: number): void {
    this.whoosh(x, y, z, 0.3, 0.16, 400, 2600);
  }

  knifeThrow(x: number, y: number, z: number): void {
    this.whoosh(x, y, z, 0.35, 0.26, 300, 3200);
    // A faint ring on the blade.
    const v = this.voice(0.12, 0.2, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.002, 1, 0.15);
    env.connect(v.out);
    this.osc(v, "sine", 1900, t, t + 0.2).connect(env);
  }

  /** Knife landing in something. */
  knifeThunk(x: number, y: number, z: number): void {
    const v = this.voice(0.5, 0.2, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.001, 1, 0.1);
    env.connect(v.out);
    const o = this.osc(v, "sine", 160, t, t + 0.14);
    o.frequency.exponentialRampToValueAtTime(70, t + 0.09);
    o.connect(env);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 800;
    const click = c.createGain();
    envelope(click.gain, t, 0.001, 0.5, 0.025);
    lp.connect(click);
    click.connect(v.out);
    this.noise(v, t, t + 0.04).connect(lp);
  }

  /** Boot on grenade. */
  kick(x: number, y: number, z: number): void {
    const v = this.voice(0.5, 0.25, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.002, 1, 0.14);
    env.connect(v.out);
    const o = this.osc(v, "sine", 110, t, t + 0.18);
    o.frequency.exponentialRampToValueAtTime(50, t + 0.12);
    o.connect(env);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 1200;
    const burst = c.createGain();
    envelope(burst.gain, t, 0.001, 0.6, 0.05);
    lp.connect(burst);
    burst.connect(v.out);
    this.noise(v, t, t + 0.07).connect(lp);
  }

  // ---- at the listener

  /** Short two-step click. */
  pickup(): void {
    const v = this.voice(0.3, 0.1);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.001, 1, 0.07);
    env.connect(v.out);
    const o = this.osc(v, "square", 1400, t, t + 0.08);
    o.frequency.setValueAtTime(2100, t + 0.035);
    o.connect(env);
  }

  /** Crisp tick. */
  hitMarker(): void {
    const v = this.voice(0.3, 0.05);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.0005, 1, 0.018);
    env.connect(v.out);
    this.osc(v, "square", 3000, t, t + 0.03).connect(env);
  }

  /** Low thud when you take damage. */
  hurt(): void {
    const v = this.voice(0.6, 0.3);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.003, 1, 0.2);
    env.connect(v.out);
    const o = this.osc(v, "sine", 80, t, t + 0.25);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.18);
    o.connect(env);
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 500;
    const burst = c.createGain();
    envelope(burst.gain, t, 0.002, 0.4, 0.06);
    lp.connect(burst);
    burst.connect(v.out);
    this.noise(v, t, t + 0.08).connect(lp);
  }

  /** Descending tone. */
  death(): void {
    const v = this.voice(0.45, 0.8);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.setValueAtTime(2400, t);
    lp.frequency.exponentialRampToValueAtTime(300, t + 0.7);
    const env = c.createGain();
    envelope(env.gain, t, 0.01, 1, 0.65);
    lp.connect(env);
    env.connect(v.out);
    const o = this.osc(v, "sawtooth", 600, t, t + 0.75);
    o.frequency.exponentialRampToValueAtTime(90, t + 0.7);
    o.connect(lp);
  }

  /** Ascending blip. */
  respawn(): void {
    const v = this.voice(0.35, 0.25);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.005, 1, 0.16);
    env.connect(v.out);
    const o = this.osc(v, "sine", 400, t, t + 0.2);
    o.frequency.exponentialRampToValueAtTime(950, t + 0.14);
    o.connect(env);
  }

  /** Countdown beep. n is the number shown; 0 is the higher, longer go beep. */
  countdown(n: number): void {
    const go = n <= 0;
    const v = this.voice(0.4, go ? 0.45 : 0.15);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.004, 1, go ? 0.38 : 0.1);
    env.connect(v.out);
    this.osc(v, "sine", go ? 1320 : 660, t, t + (go ? 0.42 : 0.13)).connect(env);
  }

  matchStart(): void {
    this.tone(523, 0.12, 0.35, 0);
    this.tone(784, 0.3, 0.4, 0.12);
  }

  matchEnd(): void {
    this.tone(392, 0.7, 0.28, 0);
    this.tone(523, 0.7, 0.28, 0);
    this.tone(659, 0.7, 0.28, 0);
    this.tone(1046, 0.5, 0.2, 0.2);
  }

  uiClick(): void {
    const v = this.voice(0.15, 0.04);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const env = c.createGain();
    envelope(env.gain, t, 0.0005, 1, 0.012);
    env.connect(v.out);
    this.osc(v, "square", 2000, t, t + 0.02).connect(env);
  }

  // ---- internals

  /** A plain sine note at the listener, used by the match jingles. */
  private tone(freq: number, dur: number, peak: number, delay: number): void {
    const v = this.voice(peak, dur + delay);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime + delay;
    const env = c.createGain();
    envelope(env.gain, t, 0.01, 1, dur - 0.01);
    env.connect(v.out);
    this.osc(v, "sine", freq, t, t + dur).connect(env);
  }

  /** Noise through a sweeping bandpass. */
  private whoosh(x: number, y: number, z: number, peak: number, dur: number, fromHz: number, toHz: number): void {
    const v = this.voice(peak, dur + 0.05, x, y, z);
    if (!v) return;
    const c = this.ctx!;
    const t = c.currentTime;
    const bp = c.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.value = 1.2;
    bp.frequency.setValueAtTime(fromHz, t);
    bp.frequency.exponentialRampToValueAtTime(toHz, t + dur * 0.6);
    bp.frequency.exponentialRampToValueAtTime(fromHz * 1.5, t + dur);
    const env = c.createGain();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(1, t + dur * 0.35);
    env.gain.exponentialRampToValueAtTime(SILENT, t + dur);
    bp.connect(env);
    env.connect(v.out);
    this.noise(v, t, t + dur + 0.02).connect(bp);
  }

  /** Distance gain and pan for a world position relative to the ear. */
  private spatial(x: number, y: number, z: number): { gain: number; pan: number } {
    const dx = x - this.ear.x, dy = y - this.ear.y, dz = z - this.ear.z;
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (d >= FAR_M) return { gain: 0, pan: 0 };
    let gain = 1;
    if (d > NEAR_M) gain = (NEAR_M / d) * clamp01((FAR_M - d) / (FAR_M - NEAR_M));
    let pan = 0;
    if (d > 0.05) {
      const rx = Math.cos(this.ear.yaw), rz = -Math.sin(this.ear.yaw);
      pan = (dx * rx + dz * rz) / d;
      // Sounds very close stay near the centre rather than jumping between ears.
      pan *= clamp01(d / NEAR_M);
      pan = Math.max(-1, Math.min(1, pan));
    }
    return { gain, pan };
  }

  private respace(h: Hiss): void {
    const c = this.ctx;
    if (!c) return;
    const v = h.voice;
    const p = v.pos ?? { x: this.ear.x, y: this.ear.y, z: this.ear.z };
    const s = this.spatial(p.x, p.y, p.z);
    const t = c.currentTime;
    v.space.gain.setTargetAtTime(s.gain, t, 0.05);
    if (v.pan) v.pan.pan.setTargetAtTime(s.pan, t, 0.05);
    v.level = h.peak * s.gain;
  }

  /**
   * Allocate a voice: out -> space -> pan -> master. Returns null when the sound is
   * out of range or would be the quietest of a full set. Positional when x is given.
   */
  private voice(peak: number, dur: number, x?: number, y?: number, z?: number): Voice | null {
    const c = this.ctx;
    const master = this.master;
    if (!c || !master) return null;
    const now = c.currentTime;
    this.voices = this.voices.filter((v) => {
      if (v.end > now) return true;
      this.kill(v);
      return false;
    });
    let level = peak, panv = 0;
    const positional = x !== undefined && y !== undefined && z !== undefined;
    if (positional) {
      const s = this.spatial(x, y, z);
      if (s.gain <= 0.002) return null;
      level = peak * s.gain;
      panv = s.pan;
    }
    if (this.voices.length >= MAX_VOICES) {
      let q = 0;
      for (let i = 1; i < this.voices.length; i++) if (this.voices[i].level < this.voices[q].level) q = i;
      if (this.voices[q].level >= level) return null;
      this.kill(this.voices[q]);
      this.voices.splice(q, 1);
    }
    const out = c.createGain();
    out.gain.value = peak;
    const space = c.createGain();
    space.gain.value = positional ? level / peak : 1;
    out.connect(space);
    let pan: StereoPannerNode | null = null;
    if (positional && typeof c.createStereoPanner === "function") {
      pan = c.createStereoPanner();
      pan.pan.value = panv;
      space.connect(pan);
      pan.connect(master);
    } else {
      space.connect(master);
    }
    const v: Voice = { out, space, pan, level, end: now + dur, sources: [] };
    if (positional) v.pos = { x, y, z };
    this.voices.push(v);
    return v;
  }

  private kill(v: Voice): void {
    for (const s of v.sources) {
      try { s.stop(); } catch { /* not started or already stopped */ }
      try { s.disconnect(); } catch { /* ignore */ }
    }
    try { v.out.disconnect(); } catch { /* ignore */ }
    try { v.space.disconnect(); } catch { /* ignore */ }
    if (v.pan) { try { v.pan.disconnect(); } catch { /* ignore */ } }
    if (v.hissId !== undefined) this.hisses.delete(v.hissId);
  }

  private osc(v: Voice, type: OscType, freq: number, t0: number, t1: number): OscillatorNode {
    const c = this.ctx!;
    const o = c.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    o.start(t0);
    if (Number.isFinite(t1)) o.stop(t1);
    v.sources.push(o);
    return o;
  }

  private noise(v: Voice, t0: number, t1: number): AudioBufferSourceNode {
    const c = this.ctx!;
    const n = c.createBufferSource();
    n.buffer = this.noiseBuffer;
    n.loop = true;
    n.start(t0);
    if (Number.isFinite(t1)) n.stop(t1);
    v.sources.push(n);
    return n;
  }
}

/** Attack to peak then an exponential decay to silence. */
function envelope(p: AudioParam, t0: number, attack: number, peak: number, decay: number): void {
  p.setValueAtTime(0, t0);
  p.linearRampToValueAtTime(Math.max(peak, SILENT), t0 + attack);
  p.exponentialRampToValueAtTime(SILENT, t0 + attack + Math.max(decay, 0.001));
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Integer hash of a position quantised to half metres, so nearby spots share a sound. */
function hash3(x: number, y: number, z: number): number {
  const ix = Math.round(x * 2) | 0, iy = Math.round(y * 2) | 0, iz = Math.round(z * 2) | 0;
  let h = (ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791);
  h = Math.imul(h ^ (h >>> 13), 0x5bd1e995);
  h ^= h >>> 15;
  return h >>> 0;
}
